/**
 * Test saved login (ENG-2065). Jacob, 2026-09-27: the per-integration login
 * already exists in connector-tools' camera-list job (`update_camera_list.py`,
 * the auto-add refresh): it logs in to the site's VMS or vendor cloud the way
 * the connector does and records the outcome on the site's status row. This
 * reads that outcome.
 */
import { RefreshStatus } from "../troubleshootingInterfaces";
import { FAMILY_LABEL, siteFamily } from "./siteFamily";

/** "Milestone", "VMS", "cloud VMS"; anything else is just "system". */
export const savedLoginSystemLabel = (
  integrationTypeName: string | null | undefined
): string => {
  const kind = siteFamily(integrationTypeName);
  return kind === "milestone" || kind === "vms-pull" || kind === "cloud-pull"
    ? FAMILY_LABEL[kind]
    : "system";
};

/**
 * A saved-login result this recent counts as evidence the site is reachable
 * (Diagnosis all-clear). It proves our side reached the VMS and the saved
 * login works, not that the connector is running.
 */
export const SAVED_LOGIN_EVIDENCE_MINUTES = 60;

/**
 * connector-tools' `initialize_customer` picks the integration by type for
 * these, whatever `auto_add` says…
 */
const BY_INTEGRATION = new Set([
  "milestone",
  "milestone_rtsp",
  "avigilon",
  "digital_watchdog",
  "exacq",
  "genetec",
  // connector-tools' Salient CompleteView integration (auto-add, ENG-2088):
  // it logs in to each of the site's DVRs to list cameras.
  "salient",
]);
/** …and by the site's `auto_add` for these. */
const BY_AUTO_ADD = new Set([
  "milestone",
  "milestone_rtsp",
  "wisenet",
  "hikvision",
  "lightapi",
  "openeye",
  "orchid",
  "eagle_eye",
  "eagle_eye_v3",
  "star4live",
  "ezcloud",
  "hikcentral",
  "adpro",
  "yoursix",
  "video_insight",
  "ajax",
  "ajax_video",
  "genetec",
]);

const norm = (value: string | null | undefined) =>
  (value || "").trim().toLowerCase();

/** Whether connector-tools can log in to this site from its saved settings. */
export const savedLoginSupported = (site: {
  integration_type_name?: string | null;
  auto_add?: string | null;
}): boolean =>
  BY_INTEGRATION.has(norm(site.integration_type_name)) ||
  BY_AUTO_ADD.has(norm(site.auto_add));

export type SavedLoginOutcome =
  | { state: "running" }
  | { state: "ok"; at: number }
  | { state: "wrong_login"; at: number; detail: string }
  | { state: "failed"; at: number; message: string; detail: string };

const seconds = (value: number | string | null | undefined): number | null => {
  const n = Number(value);
  return value != null && value !== "" && Number.isFinite(n) ? n : null;
};

/** When the row's latest refresh started (epoch seconds), or null. */
export const refreshStartedAt = (status: RefreshStatus | null) =>
  seconds(status?.last_refresh_started);

/**
 * Read a finished refresh. `running` until `last_refresh_completed` passes
 * `last_refresh_started`. connector-tools writes "Unauthorized, check
 * credentials" for a refused login and clears both fields on success.
 */
export const readSavedLoginStatus = (
  status: RefreshStatus | null
): SavedLoginOutcome => {
  const started = seconds(status?.last_refresh_started);
  const completed = seconds(status?.last_refresh_completed);
  if (started == null || completed == null || completed < started)
    return { state: "running" };
  const message = (status?.connectivity_message || "").trim();
  const detail = (status?.connectivity_reason || "").trim();
  if (!message && !detail) return { state: "ok", at: completed };
  if (
    /unauthori[sz]ed|credential|authentication failed|invalid (user|login|password)/i.test(
      `${message} ${detail}`
    )
  )
    return { state: "wrong_login", at: completed, detail };
  return {
    state: "failed",
    at: completed,
    message: message || "The login didn't complete",
    detail,
  };
};
